import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import "./Tienda.css";
import AppCardProducto from "../components/AppCardProducto";
import { getProductos, getCategoriasProductos, getConfigPagina, isApiConfigured } from "../services/api";
import ContenidoExtra from "../components/ContenidoExtra";

const DEFAULT_TITULO = "¡Lleva la aventura a casa!";
const DEFAULT_SUBTITULO =
  "Descubre nuestra colección oficial de Balloon & Ben: peluches, ropa, libros y mucho más. Cada producto está diseñado para que los más pequeños compartan la magia del campamento en cada momento del día.";

const defaultProductos = [
  {
    id: 1,
    nombre: "Peluche Ben",
    precio: "89900",
    precio_regular: null,
    precio_oferta: null,
    en_oferta: false,
    imagen: null,
    descripcion: "El adorable elefante Ben en peluche de alta calidad. Suave, resistente y perfecto para abrazar.",
    enlace: null,
    en_stock: false,
    categorias: [{ slug: "peluches", nombre: "Peluches" }],
  },
  {
    id: 2,
    nombre: "Peluche Balloon",
    precio: "89900",
    precio_regular: null,
    precio_oferta: null,
    en_oferta: false,
    imagen: null,
    descripcion: "El fiel amigo Balloon en peluche súper suave. Ideal para dormir y explorar nuevas aventuras.",
    enlace: null,
    en_stock: false,
    categorias: [{ slug: "peluches", nombre: "Peluches" }],
  },
  {
    id: 3,
    nombre: "Set de Peluches Ben + Balloon",
    precio: "159900",
    precio_regular: "189900",
    precio_oferta: "159900",
    en_oferta: true,
    imagen: null,
    descripcion: "Ben y Balloon juntos en este set especial. ¡El regalo perfecto para los fans del campamento!",
    enlace: null,
    en_stock: false,
    categorias: [{ slug: "peluches", nombre: "Peluches" }],
  },
  {
    id: 4,
    nombre: "Libro de Colorear",
    precio: "29900",
    precio_regular: null,
    precio_oferta: null,
    en_oferta: false,
    imagen: null,
    descripcion: "32 páginas para colorear con los personajes del campamento. Incluye actividades y juegos.",
    enlace: null,
    en_stock: false,
    categorias: [{ slug: "libros", nombre: "Libros" }],
  },
  {
    id: 5,
    nombre: "Camiseta Balloon & Ben",
    precio: "49900",
    precio_regular: null,
    precio_oferta: null,
    en_oferta: false,
    imagen: null,
    descripcion: "Camiseta 100% algodón con diseño exclusivo de Balloon & Ben. Disponible en tallas 2T a 10.",
    enlace: null,
    en_stock: false,
    categorias: [{ slug: "ropa", nombre: "Ropa" }],
  },
  {
    id: 6,
    nombre: "Mochila Aventuras",
    precio: "79900",
    precio_regular: null,
    precio_oferta: null,
    en_oferta: false,
    imagen: null,
    descripcion: "La mochila perfecta para explorar con Ben. Compartimento principal, bolsillo frontal y diseño resistente al agua.",
    enlace: null,
    en_stock: false,
    categorias: [{ slug: "accesorios", nombre: "Accesorios" }],
  },
];

export default function Tienda() {
  const [config, setConfig] = useState({ titulo: null, subtitulo: null, contenido_extra: null });
  const [productos, setProductos] = useState(defaultProductos);
  const [categorias, setCategorias] = useState([]);
  const [categoriaActiva, setCategoriaActiva] = useState("todos");
  const [cargando, setCargando] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const sectionVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.6,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: shouldReduceMotion ? 0.2 : 0.5, ease: "easeOut" },
    },
  };

  useEffect(() => {
    if (!isApiConfigured()) return;

    getConfigPagina("tienda")
      .then((cfg) => { if (cfg) setConfig(cfg); })
      .catch(() => {});

    getCategoriasProductos()
      .then((cats) => { if (cats.length > 0) setCategorias(cats); })
      .catch(() => {});

    setCargando(true);
    getProductos()
      .then((data) => {
        if (data && data.length > 0) setProductos(data);
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  const handleCategoria = (slug) => {
    setCategoriaActiva(slug);
    if (!isApiConfigured()) return;

    setCargando(true);
    getProductos(slug === "todos" ? null : slug)
      .then((data) => {
        if (data && data.length > 0) setProductos(data);
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  };

  const productosFiltrados =
    categoriaActiva === "todos" || !isApiConfigured()
      ? productos
      : productos.filter((p) =>
          p.categorias?.some((c) => c.slug === categoriaActiva)
        );

  return (
    <div className="tienda">
      <motion.div
        initial="hidden"
        animate="show"
        variants={sectionVariants}
      >
        <motion.div className="tienda-wrapper" variants={itemVariants}>
          <h1 className="tienda-title">{config.titulo || DEFAULT_TITULO}</h1>
        </motion.div>
        <motion.div className="tienda-descripciones" variants={itemVariants}>
          <h3 className="tienda-subtitle">{config.subtitulo || DEFAULT_SUBTITULO}</h3>
        </motion.div>
        <ContenidoExtra html={config.contenido_extra} variants={itemVariants} />
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={sectionVariants}
      >
        {/* Filtros por categoría */}
        {categorias.length > 0 && (
          <motion.div className="tienda-filtros" variants={itemVariants}>
            <button
              className={`tienda-filtro-btn${categoriaActiva === "todos" ? " tienda-filtro-btn--activo" : ""}`}
              onClick={() => handleCategoria("todos")}
            >
              Todos
            </button>
            {categorias.map((cat) => (
              <button
                key={cat.slug}
                className={`tienda-filtro-btn${categoriaActiva === cat.slug ? " tienda-filtro-btn--activo" : ""}`}
                onClick={() => handleCategoria(cat.slug)}
              >
                {cat.nombre}
              </button>
            ))}
          </motion.div>
        )}

        {/* Grid de productos */}
        <motion.div className="tienda-grid-wrap" variants={itemVariants}>
          {cargando ? (
            <div className="tienda-loading">
              <div className="tienda-spinner" />
              <p>Cargando productos...</p>
            </div>
          ) : productosFiltrados.length === 0 ? (
            <div className="tienda-vacio">
              <p>No hay productos disponibles en esta categoría.</p>
            </div>
          ) : (
            <div className="tienda-grid">
              {productosFiltrados.map((producto) => (
                <AppCardProducto
                  key={producto.id}
                  nombre={producto.nombre}
                  precio={producto.precio}
                  precio_regular={producto.precio_regular}
                  imagen={producto.imagen}
                  descripcion={producto.descripcion}
                  enlace={producto.enlace}
                  en_oferta={producto.en_oferta}
                  en_stock={producto.en_stock}
                />
              ))}
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Banner informativo si WooCommerce no está activo */}
      {!isApiConfigured() && (
        <motion.div
          className="tienda-coming-soon"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={itemVariants}
        >
          <div className="tienda-coming-soon__inner">
            <span className="tienda-coming-soon__icon">🛒</span>
            <h2 className="tienda-coming-soon__titulo">Tienda próximamente</h2>
            <p className="tienda-coming-soon__texto">
              Estamos preparando productos increíbles para ti. ¡Muy pronto podrás comprar tu merch oficial de Balloon & Ben!
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
